import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GridActionIconClickEvent, GridConfig, GridHyperlinkClickEvent, GridRowSelectedEvent } from '@inavista/components/grid';
import { environment } from 'projects/service/src/environments/environment';
import { CommonService } from '../../services/common.service';
import { UserRoles } from '../../models/app.model';
import { MasterItemcodePopupComponent } from 'projects/shared/src/lib/components/master-itemcode-popup/master-itemcode-popup.component';
import { RemoteSearchGridApi, RemoteSearchParam, provideRemoteSearchGrid, remoteSearchRow } from '../../utils/remote-search-grid-api';
import { statusPillColor } from '../../utils/status-pill-colors';
import {
    ITEMS_ADVANCED_SEARCH_URL, ITEMS_PROCEED_ACTION_ICONS, ON_HAND_PARAM, PROCEED_ACTION, buildItemsAdvancedSearchGridConfig,
    itemsAdvancedSearchColumns, onHandFilter,
} from './items-advanced-search.grid.config';

const BASE_URL = environment.apiUrl;

// The old grid's content was window.innerHeight - 300 tall, with its toolbar and header above it and the
// "Total Records" bar below. The inavista grid draws all of that inside its own box.
const GRID_CHROME_PX = 124;

@Component({
    selector: 'app-items-advanced-search',
    templateUrl: './items-advanced-search.component.html',
    styleUrls: ['./items-advanced-search.component.scss'],
    // The grid reads the old endpoints through RemoteSearchGridApi (same request, same response).
    providers: [...provideRemoteSearchGrid()],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ItemsAdvancedSearchComponent implements OnInit {

    USER: any;
    isExpanded: boolean = true;
    gridConfig: GridConfig = null;
    readonly actionIcons = ITEMS_PROCEED_ACTION_ICONS;
    selectedRowData: any;

    @Input() warehouse: string;
    @Input() category: string = 'servieable';
    @Input() module: string;
    @Input() serviceItemGroup: string;
    @Input() technicianId: string;

    @Output() onClose = new EventEmitter();
    gridHeight: number = window.innerHeight - 300 + GRID_CHROME_PX;
    private readonly gridApi = inject(RemoteSearchGridApi);

    constructor(
        private commonService: CommonService,
        private dialog: MatDialog
    ) { }

    showMasterItemCodePopup(eqData: any) {
        if (!eqData?.itemCode && !eqData?.item) return;
        const dialogRef = this.dialog.open(MasterItemcodePopupComponent, {
            panelClass: 'master-itemcode-popup-dialog',
            width: '80%',
            height: '80%',
            data: eqData
        });
        dialogRef.afterClosed().subscribe((result) => { });
    }

    ngOnInit(): void {
        this.USER = this.commonService.USER;
        // As before, the URL carries technicianId as given (also when the caller passes none).
        let url = `${BASE_URL}ServiceItem/GetServiceItemSearch?technicianId=${this.technicianId}`;
        if (this.module === 'ReturnOrder') {
            url = `${BASE_URL}ReturnOrder/GetServiceItemsForReturnOrder?warehouse=${this.warehouse}&category=${this.category}`;
        }
        // The old Query parameters.
        const parameters: RemoteSearchParam[] = [{ key: 'company', value: this.commonService.USER.company }];
        if (this.serviceItemGroup) {
            parameters.push({ key: 'serviceItemGroup', value: this.serviceItemGroup });
        }
        // On Hand (column and checkbox) only for non-ISC users.
        const showOnHand = ![UserRoles.isc, UserRoles.iscManager, UserRoles.IscSupervisor].includes(this.commonService.USER.role);
        const columns = itemsAdvancedSearchColumns(showOnHand);
        this.gridApi.set(ITEMS_ADVANCED_SEARCH_URL, {
            endpoint: url, columns, searchFields: columns.map(([c]) => c), parameters,
            paramFilters: showOnHand ? { [ON_HAND_PARAM]: onHandFilter } : {},
        });
        this.gridConfig = buildItemsAdvancedSearchGridConfig(showOnHand, statusPillColor(s => this.commonService.getStatusClass(s)));
    }

    onActionIconClick(event: GridActionIconClickEvent) {
        if (event.iconKey === PROCEED_ACTION) {
            this.onClose.emit(remoteSearchRow(event.row));
        }
    }

    // Item picks the row, as the Proceed arrow; Item Description opens the Item Information popup.
    onHyperlinkClick(event: GridHyperlinkClickEvent) {
        const row = remoteSearchRow(event.row);
        if (event.columnId === 'item') {
            this.onClose.emit(row);
        } else if (event.columnId === 'itemDescription') {
            this.showMasterItemCodePopup(row);
        }
    }

    close() {
        this.onClose.emit(null);
    }

    proceed() {
        this.onClose.emit(this.selectedRowData);
    }

    rowSelected(event: GridRowSelectedEvent) {
        this.selectedRowData = remoteSearchRow(event.row);
    }
}
